import React, { useState, useCallback } from 'react';
import {
  View,
  ScrollView,
  Pressable,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import CustomText from '../components/CustomText';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AppButton from '../components/AppButton';
import Util from '../util/util.js';
import { formatMediumDate } from '../util/format';
import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

const DrawingManagement = ({ navigation, route }) => {
  const { t } = useTranslation('drawings');

  const { containerStyle } = useResponsiveStyles();
  const { organization, admin, userAdmin, orgAdmin } = route.params || {};

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [drawings, setDrawings] = useState([]);
  const [filter, setFilter] = useState('active'); // 'all', 'active', 'ended'

  useFocusEffect(
    useCallback(() => {
      loadDrawings();
    }, [])
  );

  const loadDrawings = async () => {
    try {
      setLoading(true);
      const response = await Util.get('Resty', '/items/drawings');
      if (response && !response.hasError) {
        setDrawings(response.items || []);
      }
    } catch (error) {
      console.log('Error loading drawings:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    loadDrawings();
  };

  const filteredDrawings = drawings.filter((d) => {
    if (filter === 'all') return true;
    return d.status === filter;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return t('management.notAvailable');
    // Use UTC to avoid timezone day-shift issues
    return formatMediumDate(timestamp, { timeZone: 'UTC' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return AppStyles.colorSet.success;
      case 'ended':
        return AppStyles.colorSet.info;
      case 'cancelled':
        return AppStyles.colorSet.error;
      default:
        return AppStyles.colorSet.textSecondary;
    }
  };

  const renderFilterButton = (label, value) => (
    <Pressable
      style={({ hovered, focused }) => [
        styles.filterButton,
        { cursor: 'pointer' },
        filter !== value && (hovered || focused) && styles.filterButtonHovered,
        filter === value && styles.filterButtonActive,
      ]}
      onPress={() => setFilter(value)}
    >
      <CustomText
        style={[
          styles.filterButtonText,
          filter === value && styles.filterButtonTextActive,
        ]}
      >
        {label}
      </CustomText>
    </Pressable>
  );

  const renderDrawingCard = (drawing) => (
    <Pressable
      key={drawing.drawingId}
      style={({ hovered, focused }) => [
        styles.drawingCard,
        { cursor: 'pointer' },
        (hovered || focused) && styles.drawingCardHovered,
      ]}
      onPress={() =>
        navigation.navigate('DrawingDetails', { drawingId: drawing.drawingId, organization, admin, userAdmin, orgAdmin })
      }
    >
      <View style={styles.cardHeader}>
        <CustomText style={styles.drawingName}>{drawing.name}</CustomText>
        <View
          style={[
            styles.statusBadge,
            { backgroundColor: getStatusColor(drawing.status) },
          ]}
        >
          <CustomText style={styles.statusText}>
            {drawing.status.toUpperCase()}
          </CustomText>
        </View>
      </View>

      <CustomText style={styles.drawingDescription} numberOfLines={2}>
        {drawing.description || t('management.noDescription')}
      </CustomText>

      <View style={styles.cardDetails}>
        <View style={styles.detailRow}>
          <CustomText style={styles.detailLabel}>{t('management.organizations')}</CustomText>
          <CustomText style={styles.detailValue}>
            {drawing.organizations?.length || 0}
          </CustomText>
        </View>
        <View style={styles.detailRow}>
          <CustomText style={styles.detailLabel}>{t('management.participants')}</CustomText>
          <CustomText style={styles.detailValue}>
            {drawing.totalParticipants || 0}
          </CustomText>
        </View>
        <View style={styles.detailRow}>
          <CustomText style={styles.detailLabel}>{t('management.period')}</CustomText>
          <CustomText style={styles.detailValue}>
            {formatDate(drawing.startDate)} - {formatDate(drawing.endDate)}
          </CustomText>
        </View>
      </View>

      {drawing.status === 'ended' && drawing.winnerId && (
        <View style={styles.winnerSection}>
          <CustomText style={styles.winnerLabel}>{t('management.winner')}</CustomText>
          <CustomText style={styles.winnerEmail}>{drawing.winnerId}</CustomText>
        </View>
      )}
    </Pressable>
  );

  return (
    <View style={[containerStyle, styles.container]}>
      <Header organization={organization} admin={admin} userAdmin={userAdmin} type="pop" />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <View style={styles.headerRow}>
          <CustomText style={styles.pageTitle}>{t('management.title')}</CustomText>
          <AppButton
            title={t('management.newDrawing')}
            onPress={() => navigation.navigate('CreateDrawing', { organization, admin, userAdmin, orgAdmin })}
            buttonStyle={styles.createButton}
          />
        </View>

        <View style={styles.filterContainer}>
          {renderFilterButton(t('management.filterAll'), 'all')}
          {renderFilterButton(t('management.filterActive'), 'active')}
          {renderFilterButton(t('management.filterEnded'), 'ended')}
        </View>

        {loading ? (
          <ActivityIndicator
            size="large"
            color={AppStyles.colorSet.success}
            style={styles.loader}
          />
        ) : filteredDrawings.length === 0 ? (
          <View style={styles.emptyState}>
            <CustomText style={styles.emptyIcon}>🎯</CustomText>
            <CustomText style={styles.emptyText}>
              {filter === 'all'
                ? t('management.empty')
                : t(filter === 'active' ? 'management.emptyActive' : 'management.emptyEnded')}
            </CustomText>
            <CustomText style={styles.emptySubtext}>
              {filter === 'all'
                ? t('management.emptyHint')
                : t('management.emptyFiltered')}
            </CustomText>
          </View>
        ) : (
          filteredDrawings.map(renderDrawingCard)
        )}
      </ScrollView>
      <Footer
        organization={organization}
        admin={admin}
        userAdmin={userAdmin}
        orgAdmin={orgAdmin}
      />
    </View>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: AppStyles.colorSet.inputBackground,
  },
  scrollView: {
    flex: 1,
    width: '100%',
  },
  content: {
    padding: 20,
    paddingBottom: 100,
    flexGrow: 1,
    alignItems: 'stretch',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: AppStyles.colorSet.textPrimary,
  },
  createButton: {
    width: 'auto',
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  filterContainer: {
    flexDirection: 'row',
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: AppStyles.colorSet.whiteColor,
    marginRight: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: AppStyles.borderSet.default,
  },
  filterButtonActive: {
    backgroundColor: AppStyles.colorSet.success,
    borderColor: AppStyles.colorSet.success,
  },
  filterButtonHovered: {
    borderColor: AppStyles.colorSet.brandPrimary,
    backgroundColor: AppStyles.stateSet.hover,
  },
  filterButtonText: {
    fontSize: 14,
    color: AppStyles.colorSet.textSecondary,
  },
  filterButtonTextActive: {
    color: AppStyles.colorSet.whiteColor,
  },
  loader: {
    marginTop: 40,
  },
  emptyState: {
    alignItems: 'center',
    marginTop: 60,
    paddingHorizontal: 20,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: AppStyles.colorSet.textPrimary,
    textAlign: 'center',
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: AppStyles.colorSet.textSecondary,
    textAlign: 'center',
  },
  drawingCard: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  drawingCardHovered: {
    borderWidth: 1,
    borderColor: AppStyles.colorSet.brandPrimary,
    shadowOpacity: 0.18,
    shadowRadius: 10,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  drawingName: {
    fontSize: 18,
    fontWeight: '600',
    color: AppStyles.colorSet.textPrimary,
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: AppStyles.colorSet.whiteColor,
  },
  drawingDescription: {
    fontSize: 14,
    color: AppStyles.colorSet.textSecondary,
    marginBottom: 12,
  },
  cardDetails: {
    borderTopWidth: 1,
    borderTopColor: AppStyles.borderSet.default,
    paddingTop: 12,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  detailLabel: {
    fontSize: 14,
    color: AppStyles.colorSet.textSecondary,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '500',
    color: AppStyles.colorSet.textPrimary,
  },
  winnerSection: {
    marginTop: 12,
    padding: 12,
    backgroundColor: AppStyles.colorSet.successLight,
    borderRadius: 8,
  },
  winnerLabel: {
    fontSize: 12,
    color: AppStyles.colorSet.success,
    fontWeight: '600',
  },
  winnerEmail: {
    fontSize: 14,
    color: AppStyles.colorSet.textPrimary,
    fontWeight: '500',
  },
};

export default DrawingManagement;
